import { Navigate } from "react-router";
import { toast } from "react-toastify";
import { useAuth } from "../context/useAuth";
import { useActiveHospital } from "@features/hospitals";
import RouteLoader from "../../../components/ui/RouteLoader";
import { computeCanAccess } from "../access/canAccess";
import { homePathFor } from "../access/homePathFor";
import { ROLE_HOSPITALS } from "../../../types/role";

interface ValidateAuthProps {
  children: React.ReactNode;
  roles?: number[];
  permissions?: number[];
  /** Códigos de permissão exigidos (basta um); ver `AccessRequirement.permissionCodes`. */
  permissionCodes?: string[];
  hospitalNeedsToBeActive?: boolean;
  /**
   * Papéis que a rota NÃO atende, mesmo sendo aberta aos demais. Existe para a
   * exceção do médico interno na home composta (ADR home-composta-inicio): a
   * regra estava só em comentário no Router e por isso não valia.
   */
  excludeRoles?: number[];
}

export function ValidateAuth({
  children,
  roles = [],
  permissions = [],
  permissionCodes = [],
  hospitalNeedsToBeActive = false,
  excludeRoles = [],
}: ValidateAuthProps) {
  const { isLoading, isAuthenticated, user } = useAuth();
  const activeHospital = useActiveHospital();

  if (isLoading) {
    return <RouteLoader message="Carregando permissões..." />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const redirectPath = homePathFor(user);

  if (user.role_id != null && excludeRoles.includes(user.role_id)) {
    return <Navigate to={redirectPath} replace />;
  }

  // P20EE-199: a hospital user's rights in the active hospital arrive with the scoped token (`POST /hospitals/active`),
  // after the hospital itself is read from storage. On a reload or a deep link the guard ran in that window and denied
  // `/observabilidade` to a master holding `audit:read` there, while the menu, computed later, offered it (dev,
  // 2026-10-23). The window starts before the hospital is known at all (the stored one is checked asynchronously), so
  // the wait does not need its id: while the hospital context says the scope is not ready, wait instead of deciding.
  if (user.role_id === ROLE_HOSPITALS && activeHospital != null && activeHospital.scopeReady === false) {
    return <RouteLoader message="Carregando permissões..." />;
  }

  // Fonte única da regra de acesso (bypass superadmin de ROLE_OPERATION + escopo de
  // permissão/procedência de hospital/convênio) — compartilhada com a home composta.
  const allowed = computeCanAccess(
    user,
    activeHospital?.isProcedenciaActive,
    { roles, permissions, permissionCodes, hospitalNeedsToBeActive },
  );

  if (!allowed) {
    // A negação existia, mas era muda: `<Navigate replace>` jogava a pessoa na home sem dizer nada,
    // e quem tentava abrir o cadastro de usuário sem ser master achava que a tela estava quebrada.
    // Mesmo padrão (e mesmo texto) do 403 de leitura no PageErrorBoundary; `toastId` evita duplicar
    // quando o StrictMode invoca o render duas vezes.
    toast.info("Você não tem acesso a esta página.", { toastId: "route-forbidden" });
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}
